import BaseViewService from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-service';
import {
    IPcLeftThumbnailListProps,
    IPcLeftThumbnailListService,
    SLIDE_WIDTH,
} from '@src/slide/application/service/pc-left-thumbnail-list/core/type';
import BaseViewModel from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-model';
import PcLeftThumbnailListModel from '@src/slide/application/service/pc-left-thumbnail-list/core/pc-left-thumbnail-list-model';
import changeFocusToCanvas, { getSlideWidthDiff } from '@com/pc-left-thumbnail-list/utils';
import {
    IPcLeftThumbnailListEditServiceDelegates,
    IPcLeftThumbnailListServiceDelegates,
    IPcLeftThumbnailListViewerServiceDelegates,
} from '@src/slide/application/service/pc-left-thumbnail-list/core/pc-left-thumbnail-list-service.interface';
import { browser } from '@utils/agent';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import DragEventHandler from '@src/slide/application/service/pc-left-thumbnail-list/core/event-handler/drag-event-handler';
import DropEventHandler from '@src/slide/application/service/pc-left-thumbnail-list/core/event-handler/drop-event-handler';
import MouseEventHandler from '@src/slide/application/service/pc-left-thumbnail-list/core/event-handler/mouse-event-handler';
import {
    DragEventDelegate,
} from '@src/slide/application/service/pc-left-thumbnail-list/core/event-handler/interface/drag-event-delegate';
import {
    DropEventDelegate,
} from '@src/slide/application/service/pc-left-thumbnail-list/core/event-handler/interface/drop-event-delegate';
import {
    MouseEventDelegate,
} from '@src/slide/application/service/pc-left-thumbnail-list/core/event-handler/interface/mouse-event-delegate';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ISectionModel } from '@src/slide/domain/ui-model/element-modeles/Interface/section-model.interface';
import ClearanceUtils from '@src/slide/domain/ui-model/utils/clearance-utils';
import SectionTitle from '@com/pc-left-thumbnail-list/list-view/section-title';
import { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { reaction } from 'mobx';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { EditEvents } from '@src/slide/domain/slide-app/core/interfaces/event.interface';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import _flatten from 'lodash/flatten';
import { INSERT_SLIDE_ORIGIN_TYPE } from '@src/js/constant';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import GapModel from '@src/slide/domain/ui-model/element-modeles/gap-model';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { ISectionProperty } from '@src/slide/domain/ui-model/element-modeles/property/section';
import { ISectionManager } from '@src/slide/application/service/section-manager/section-manager.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import {
    IUserPriviledgeService,
} from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import {
    IMasterLayoutEditService,
} from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import throttle from 'lodash/throttle';
import { SlideSizeType } from '@src/slide/domain/ui-model/element-modeles/property/slide-size/slide-size.interface';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { IStatePointEnum } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

/**
 * 左侧缩略图提前转换的页面数量
 */
const SLIDE_LIST_MORE_CONVERT_PAGE_NUMBER = 5;

export default class PcLeftThumbnailListService extends BaseViewService<IPcLeftThumbnailListProps>
    implements IPcLeftThumbnailListService, DragEventDelegate, DropEventDelegate, MouseEventDelegate {
    // model
    declare protected model: PcLeftThumbnailListModel;
    // service
    private services: IPcLeftThumbnailListServiceDelegates = { };

    private shouldScrollToIndex: number;
    private mouseEventHandler: MouseEventHandler;
    private dropEventHandler: DropEventHandler;
    private dragEventHandler: DragEventHandler;
    private draggingSlideList: [];   // 拖拽时，渲染的slideList
    private dragPos;  // 拖拽实时位置
    private draggingArray: any[];  // 拖拽的slide，可以有多个
    private scrollAmount;

    private mixSlidesReaction;
    private slidesReaction;
    private mastersReaction;
    private slideClearanceReaction;
    private layoutsReaction;
    private sectionListReaction;
    private sectionFoldedStateReaction;
    private forceRerenderOnSectionChangeReaction;
    private selectedSectionIndexReaction;
    private nameEditingIndexReaction;
    private slideSizeReaction;
    private slideSelectedReaction;
    private slideIndexReaction;
    private currentSlideReaction;
    private statesReaction;
    private slideNumberStartIndexReaction;

    public setEditServiceDelegates(services: IPcLeftThumbnailListEditServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public setViewerServiceDelegates(services: IPcLeftThumbnailListViewerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public getProps() {
        return this.props;
    }

    public getCellHeight() {
        return this.model.slideHeight;
    }

    public getSlideSectionListHeights(): [number, number][] {
        const { sectionList } = this.model;
        const { sectionFoldedState } = this.getSectionManager() || { };
        const slides = this.getSlideList();
        if (!sectionList || sectionList.length === 0) {
            return Array.from<ISlideModel, [number, number]>(slides, (_, index) => [this.getCellHeight(), index]);
        }
        const res: [number, number][] = [];
        (sectionList as ISectionModel[]).forEach((section, sectionIndex: number, sectionList) => {
            let clearanceIndex = ClearanceUtils.calcClearanceIndexFromSectionIndex(sectionIndex, sectionList);
            const isFolded = sectionFoldedState?.[sectionIndex] ?? false;
            if (isFolded) {
                clearanceIndex += section.numSlides;
            }
            res.push([SectionTitle.SectionTitleHeight, clearanceIndex]);
            if (!isFolded) {
                let sectionSize = section.numSlides;
                if (sectionIndex === sectionList.length - 1) {
                    sectionSize = this.getLastSectionSizeForCompatibility(sectionList);
                }
                Array.from({ length: sectionSize })
                    .forEach((_, index) => res.push([this.getCellHeight(), clearanceIndex + index + 1]));
            }
        });
        return res;
    }

    public getSectionListHeights(): number[] {
        const { sectionList } = this.model;
        if (!sectionList || sectionList.length === 0) {
            return [];
        }
        return Array.from(sectionList, () => SectionTitle.SectionTitleHeight);
    }

    public getCellID = item => item.key;

    public handleCellAppear = (cell) => {
        const model = this.getDataList().find(model => model.key === cell.id);
        if (!model) return;
        const isCurrentSlide = this.getCurList().find(model => model.key === cell.id);
        this.services.slideRenderStateManager?.addVisibleSlide(model);

        if (model.asyncConvertChildren) {
            const priority = isCurrentSlide
                ? TaskPriority.HIGH : TaskPriority.HIGH - 1;
            model.startConvertChildrenTask(priority);
        } else {
            if (!model.displayChildrenElement) {
                model.displayChildrenElement = true;
            }
        }
    };

    public handleCellDisappear = (cell: { id: string }) => {
        const { uiDataService } = this.services;
        // 从 slide/master/layout中查找
        const model = uiDataService?.slideModelList.find(model => model.key === cell.id)
            || uiDataService?.masterModelList.find(model => model.key === cell.id)
            || uiDataService?.layoutModelList.find(model => model.key === cell.id);
        this.services.slideRenderStateManager?.removeVisibleSlide(cell.id);
        if (!model) return;
        // slide.adjustConvertTaskPriority(TaskPriority.HIGH - 2);
        model.cancelConvertChildrenTask();
    };

    public getCurGroupWrapperStyle = () => this.services.masterLayoutStyleService?.getCurGroupWrapperStyle();

    public handleVisibleCellIDsChange = (visibleCellIDs: Array<string>) => {
        const shouldConvertModels = this.getShouldConvertModels(visibleCellIDs);
        shouldConvertModels.forEach((itemModel) => {
            if (!itemModel.childrenConverted
                && itemModel.asyncConvertChildren) {
                itemModel.startConvertChildrenTask(TaskPriority.HIGH);
            }
        });
    };

    public getSelectedArray() {
        const { selectedArray = [] } = this.services.selectionService?.selectedSlides || { };
        return selectedArray;
    }

    public isModelDragging(model): boolean {
        return this.model.currentDragIndex !== -1
            && this.services.selectionService?.selectedSlides.indexOfObject(model) !== -1;
    }

    public getRenderCellIndex(slideIndex: number, type: string) {
        if (type === EDIT_TYPE.SLIDE) {
            return slideIndex + (this.services.uiDataService?.presentation.firstSlideNum ?? 0) - 1;
        }
        return slideIndex;
    }

    public getSizeType(): SlideSizeType {
        return this.getUiDataService()?.presentation.props.slideSizeType ?? SlideSizeType.SLIDE_SIZE_16_9;
    }

    public getCurList() {
        switch (this.model.currentSlideType) {
            case UIModelType.MASTER:
                return this.model.masterModelList;
            case UIModelType.LAYOUT:
                return this.model.layoutModelList;
            default:
                return this.model.slideModelList;
        }
    }

    public getDataList(): Array<ISlideModel> {
        if (this.model.editType === EDIT_TYPE.MASTER_LAYOUT) {
            // @ts-ignore
            return this.model.masterModelList.concat(this.model.layoutModelList);
        }
        return this.model.slideModelList;
    }

    public getListView() {
        return this.listWrapper;
    }

    public getDraggingItemList() {
        return this.draggingSlideList;
    }

    public draggable() {
        const priviledgeService = this.getUserPriviledgeService();
        if (!priviledgeService) return false;
        return priviledgeService.pptEditorState <= PPTEditorStateType.EDITABLE;
    }

    // item拖拽回调
    public onDragStart(dragInfo) {
        document.body.style.cursor = 'grabbing';
        const {
            currentDragIndex,
            originalDragIndex,
            originalDragItem,
            draggingArray,
            dragPos,
            isSectionDrag,
        } = dragInfo;
        this.draggingArray = draggingArray;
        this.dragPos = dragPos;
        this.updateModel<PcLeftThumbnailListModel>({
            currentDragIndex,
            originalDragIndex,
            originalDragSlide: originalDragItem,
            isPressed: true,
            isSectionDrag,
        });
    }

    public onSectionTitleDragStart(dragInfo) {
        document.body.style.cursor = 'grabbing';
        const { currentDragIndex, originalDragIndex, originalDragItem } = dragInfo;
        this.updateModel<PcLeftThumbnailListModel>({
            currentDragIndex,
            originalDragIndex,
            originalDragSlide: originalDragItem,
            isPressed: true,
        });
    }

    public onDragging(dragInfo) {
        const { outside, scrollAmount, isSectionDrag } = dragInfo;
        let { currentDragIndex } = dragInfo;
        if (isSectionDrag && currentDragIndex !== -1) {
            currentDragIndex = this.fixDragIndexOnSectionDrag(dragInfo, currentDragIndex);
        }
        // 拖拽不改变选中的页，所以清除forMouseUp,避免mouseEventHandler在mouseUp时设置选中页
        this.mouseEventHandler.clearForMouseUp();
        this.scrollAmount = scrollAmount;
        this.updateModel<PcLeftThumbnailListModel>({
            currentDragIndex,
            outside,
        });
    }

    public onDragEnd(dragInfo) {
        document.body.style.cursor = '';

        this.draggingArray = [];
        this.scrollAmount = 0;
        const { pptEditorState = PPTEditorStateType.SELECTABLE } = this.getUserPriviledgeService() || { };
        if (pptEditorState > PPTEditorStateType.EDITABLE) {
            this.services.tipsManager?.showCannotEditTips();

            this.updateModel<PcLeftThumbnailListModel>({
                originalDragSlide: null,
                currentDragIndex: -1,
                originalDragIndex: -1,
                isPressed: false,
                isSectionDrag: false,
            });
            return;
        }
        this.handleMoveSlide(dragInfo);
        this.updateModel<PcLeftThumbnailListModel>({
            originalDragSlide: null,
            currentDragIndex: -1,
            originalDragIndex: -1,
            isPressed: false,
            isSectionDrag: false,
        });
    }

    public unfoldCorrespondingSection(clearanceIndex: number): void {
        const sectionIndex = ClearanceUtils.calcSectionIndexFromClearanceIndex(clearanceIndex, this.model.sectionList);
        if (this.model.sectionFoldedState[sectionIndex]) {
            const { sectionManager } = this.services;
            if (sectionManager?.sectionFoldedState) {
                sectionManager.sectionFoldedState[sectionIndex] = false;
                this.viewDelegate?.forceRender();
            }
        }
    }

    public getSectionList(): ISectionModel[] | undefined {
        return this.model.sectionList;
    }

    /* ---------------end------------------------*/

    public updateDraggingSlideList(list: []) {
        this.draggingSlideList = list;
    }

    public getModifiedSlidesInfos(): any[] {
        const { historyManager } = this.services;
        const modifiedSlides = historyManager?.modification || [];
        return modifiedSlides.map((modify, index) => {
            if (modify.length > 0) {
                const lastId = modify[modify.length - 1];
                const author = historyManager?.getAuthor(lastId);
                const authorName = author?.name;
                const color = historyManager?.getColorForId(lastId);
                const y = this.model.historyTipsTops[index] - this.model.historyTipsScrollTop;
                return { authorName, color, y };
            }
            return null;
        });
    }

    public componentDidMount() {
        this.dropEventHandler.registerDragEvent();
        this.services.imgLoaderManager?.setScrollTarget(this.listWrapper);

        const uiDataService = this.services?.uiDataService;
        this.mixSlidesReaction = reaction(() => uiDataService?.slideModelList, slides => this.handleListChange('slides', slides));
        this.slidesReaction = reaction(() => uiDataService?.slideModelList, slideModelList => this.handleListChange('slideModelList', slideModelList));
        this.mastersReaction = reaction(() => uiDataService?.masterModelList, masterModelList => this.handleListChange('masterModelList', masterModelList));
        this.layoutsReaction = reaction(() => uiDataService?.layoutModelList, layoutModelList => this.handleListChange('layoutModelList', layoutModelList));
        this.sectionListReaction = reaction(
            () => uiDataService?.presentation.sectionList,
            sectionList => this.handleSectionListChange(sectionList),
        );
        this.sectionFoldedStateReaction = reaction(
            () => this.services.sectionManager?.sectionFoldedState,
            sectionFoldedState => this.handleSectionFoldedStateChange(sectionFoldedState),
        );
        this.forceRerenderOnSectionChangeReaction = reaction(
            () => this.services.sectionManager?.onSectionsChangeAction,
            () => this.viewDelegate?.forceRender(),
        );
        this.selectedSectionIndexReaction = reaction(
            () => this.services.sectionManager?.selectedSectionIndex,
            () => this.viewDelegate?.forceRender(),
        );
        this.nameEditingIndexReaction = reaction(
            () => this.services.sectionManager?.nameEditingIndex,
            () => this.viewDelegate?.forceRender(),
        );
        this.slideClearanceReaction = reaction(
            () => this.services.selectionService?.slideClearanceIndex,
            slideClearanceIndex => this.handleSlideClearanceIndexChange(slideClearanceIndex ?? 0),
        );
        this.handleSectionListChange(uiDataService?.presentation.sectionList);
        this.handleSectionFoldedStateChange(this.services.sectionManager?.sectionFoldedState);
        this.statesReaction = reaction(
            () => [
                this.getMasterLayoutEditService()?.editType,
                this.services.selectionService?.currentSlideType,
            ],
            ([editType, currentSlideType]) => {
                editType = editType ?? EDIT_TYPE.SLIDE;
                if (editType === EDIT_TYPE.MASTER_LAYOUT) {
                    const { masterModelList } = this.getUiDataService() || { };
                    const { layoutModelList } = this.getUiDataService() || { };
                    this.updateModel<PcLeftThumbnailListModel>({
                        editType,
                        masterModelList,
                        layoutModelList,
                        currentSlideType,
                    });
                } else {
                    this.updateModel<PcLeftThumbnailListModel>({
                        editType,
                        currentSlideType,
                    });
                }
            },
        );
        this.slideSizeReaction = reaction(() => (
            [
                this.getUiDataService()?.presentation.props.slideSizeType,
                this.getUiDataService()?.presentation.props.slideWidth,
                this.getUiDataService()?.presentation.props.slideHeight,
            ]
        ), () => {
            this.handleSlideSizeChange();
        });

        this.slideSelectedReaction = reaction(() => {
            // store 不能在 reaction 外面解构，否则检测不到变化
            const { selectedSlides } = this.services.selectionService || { };

            return {
                // 选择的 slide 数组长度也需要被监听
                selArray: selectedSlides?.selectedArray,
                length: selectedSlides?.length,
            };
        }, () => {
            this.clearClearanceSelectIndex();
        });

        this.slideIndexReaction = reaction(() => (
            this.services.selectionService?.needClearSelectedSlideClearanceFlag
        ), () => {
            this.clearClearanceSelectIndex();
        });

        this.currentSlideReaction = reaction(() => (
            this.services.selectionService?.currentSlideIndex
        ), (currentSlideIndex) => {
            this.handleCurSlideChange(currentSlideIndex);
        });

        const lockCb = (lockInfo) => {
            this.updateModel<PcLeftThumbnailListModel>({
                lockInfo,
            });
        };
        const unlockCb = (unlockInfo) => {
            this.handleUnLock(unlockInfo);
        };
        const lockInfoUpdateCb = (lockInfo) => {
            const lockManager = OnlineEditServices.CooperationLockManager();
            this.updateModel<PcLeftThumbnailListModel>({
                lockInfo: lockInfo || lockManager?.lockInfo,
            });
        };
        SlideDataServices.EventManager.promise().then((event) => {
            event.on(EditEvents.Lock, lockCb);
            event.on(EditEvents.Unlock, unlockCb);
            event.on(EditEvents.LockInfoUpdate, lockInfoUpdateCb);
        });

        this.slideNumberStartIndexReaction = reaction(() => (
            this.getUiDataService()?.presentation.firstSlideNum
        ), () => {
            this.updateModel<PcLeftThumbnailListModel>({
                slideWidth: SLIDE_WIDTH - getSlideWidthDiff(this.getUiDataService()),
            });
        });
    }

    public componentWillUnmount() {
        this.mixSlidesReaction?.();
        this.slidesReaction?.();
        this.slideSelectedReaction?.();
        this.mastersReaction?.();
        this.layoutsReaction?.();
        this.slideClearanceReaction?.();
        this.sectionListReaction?.();
        this.sectionFoldedStateReaction?.();
        this.forceRerenderOnSectionChangeReaction?.();
        this.selectedSectionIndexReaction?.();
        this.nameEditingIndexReaction?.();
        this.slideIndexReaction?.();
        this.currentSlideReaction?.();
        this.slideSizeReaction?.();
        this.slideNumberStartIndexReaction?.();
        // 移除 iframe 中的拖拽事件
        this.dropEventHandler.unRegisterDragEvent();
    }

    public hasSections(): boolean {
        return !!this.model.sectionList?.length;
    }

    public shouldComponentUpdate(nextProps) {
        return nextProps.shouldUpdate !== false;
    }

    // eslint-disable-next-line react/no-deprecated
    public componentWillUpdate(nextProps, nextState) {
        this.updateModelBeforeRender();
        this.clearClearanceSelectIndex(false);
    }

    public componentDidUpdate() {
        const { scrollAmount } = this;

        if (scrollAmount) {
            this.listWrapper.scrollTop += scrollAmount;
        }
        if (this.shouldScrollToIndex !== -1) {
            setTimeout(() => {
                this.scrollToSlideIndex(this.shouldScrollToIndex);
                this.shouldScrollToIndex = -1;
            }, 10);
        }
        if (this.draggingDiv) {
            if (this.model.originalDragSlide) {
                this.changeDraggingSlides();
            } else if (this.model.isSectionDrag) {
                this.changeDraggingSectionTitle();
            }
        }
    }

    /**
     * 清除间隙选中线
     */
    public clearClearance() {
        if (this.model.slideClearanceIndex >= 0) {
            this.updateModel<PcLeftThumbnailListModel>({
                slideClearanceIndex: -1,
            });
        }
    }

    public getSectionFoldedState(): boolean[] | null {
        return this.getSectionManager()?.sectionFoldedState ?? null;
    }

    public setSectionName(sectionIndex: number, name: string) {
        const sectionProperty = this.getSectionProperty();
        const sectionManager = this.getSectionManager();
        if (sectionManager && sectionProperty) {
            sectionProperty.renameSection(sectionIndex, name);
            sectionManager.nameEditingIndex = -1;
        }
    }

    public toggleSectionFolded(sectionIndex: number) {
        const sectionFoldedState = this.getSectionFoldedState();
        if (!sectionFoldedState) {
            return;
        }
        const newSectionFoldedState = [...sectionFoldedState];
        newSectionFoldedState[sectionIndex] = !newSectionFoldedState[sectionIndex];
        const sectionManager = this.getSectionManager();
        if (sectionManager) {
            sectionManager.sectionFoldedState = newSectionFoldedState;
        }
    }

    public isSectionSelected(sectionIndex: number): boolean {
        const sectionManager = this.getSectionManager();
        return sectionManager?.selectedSectionIndex === sectionIndex;
    }

    public setSectionSelected(sectionIndex: number) {
        const sectionManager = this.getSectionManager();
        if (sectionManager) {
            sectionManager.selectedSectionIndex = sectionIndex;
            this.clearClearanceSelectIndex();
            this.clearClearance();
        }
    }

    public getSelectedSectionIndex(): number {
        return this.getSectionManager()?.selectedSectionIndex ?? -1;
    }

    public getSectionNameEditingIndex(): number {
        return this.getSectionManager()?.nameEditingIndex ?? -1;
    }

    public setSectionNameEditingIndex(sectionIndex: number, isEditingName: boolean) {
        const sectionManager = this.getSectionManager();
        if (sectionManager) {
            sectionManager.nameEditingIndex = isEditingName ? sectionIndex : -1;
        }
    }

    public selectSection(sectionIndex: number) {
        const sectionProperty = this.getSectionProperty();
        const sectionManager = this.getSectionManager();
        if (sectionProperty && sectionManager) {
            const [slideModels, beginSlideIndex] = sectionProperty.
                getSectionSlideModelsAndBeginSlideIndex(sectionIndex);
            sectionManager.selectSection(sectionIndex, beginSlideIndex, slideModels);
        }
    }

    public handleClickClearance = (e: MouseEvent, clearanceIndex: number): void => {
        const isShiftKey = e?.shiftKey;  // 是否按住shift
        const { selectionService } = this.services;
        // 母版编辑态选择间隙时，不仅要动态切换slideIndex，还要区分master和layout两种情况
        if (this.getMasterLayoutEditService()?.isMasterEditType) {
            const masterLayoutList = this.getSlideList(); // 平铺的母版版式列表
            const currentSlide = masterLayoutList[clearanceIndex]; // 根据间隙 id 获取对应的页 model
            // 计算母版和版式在各自数组中对应的 index
            const slideIndex = {
                [UIModelType.MASTER]: this.model.masterModelList.findIndex(i => i.id === currentSlide.id),
                [UIModelType.LAYOUT]: this.model.layoutModelList.findIndex(i => i.id === currentSlide.id),
            }[currentSlide.type] ?? 0;
            if (isShiftKey) { // 按住shift时，触发点击
                this.mouseEventHandler.handleMouseDown(e, slideIndex, currentSlide);
                return;
            }
            selectionService?.selectedSlides.clear();
            selectionService?.changeCurrentSlideIndexByUserAction(slideIndex);
            selectionService?.updateCurrentSlideType(currentSlide.type);
            selectionService!.slideClearanceIndex = clearanceIndex;
            this.updateModel<PcLeftThumbnailListModel>({
                slideClearanceIndex: clearanceIndex,
            });
            return;
        }
        const slideIndex = Math.max(
            0,
            ClearanceUtils.calcSlideIndexFromClearanceIndex(clearanceIndex, this.model.sectionList) - 1,
        );
        if (isShiftKey) {  // 按住shift时，触发点击
            const slideList = this.getSlideList(); // 平铺的母版版式列表
            this.mouseEventHandler.handleMouseDown(e, slideIndex, slideList[slideIndex]);
            return;
        }
        this.handleCellAppear({ id: this.getUiDataService()?.presentation.children[slideIndex]?.key });
        this.getMenuManager()?.hideAllMenu();
        this.services.selectionService?.selectedSlides.clear();
        this.services.selectionService?.changeCurrentSlideIndexByUserAction(slideIndex);
        this.services.sectionManager!.selectedSectionIndex = -1;
        this.services.selectionService!.slideClearanceIndex = clearanceIndex;
        this.updateModel<PcLeftThumbnailListModel>({
            slideClearanceIndex: clearanceIndex,
        });
        if (this.services.sectionManager?.nameEditingIndex !== -1) {
            this.services.sectionManager!.nameEditingIndex = -1;
            changeFocusToCanvas();
        }
    };

    public clearClearanceSelectIndex = (needRender = true) => {
        needRender && this.viewDelegate?.forceRender(); // 刷新选中态
        this.services.selectionService!.slideClearanceIndex = -1;
    };

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public handleSlideListScroll = throttle(() => {
        if (this.services.historyManager?.isShowingHistory && this.listWrapper) {
            this.updateModel<PcLeftThumbnailListModel>({
                historyTipsScrollTop: this.listWrapper?.scrollTop,
            });
        }
    }, 20);

    public handleMouseDown = (ev, listIndex: number, model: ISlideModel) => {
        const clearanceIndex = ClearanceUtils.calcClearanceIndexFromSlideIndex(listIndex, this.model.sectionList) - 1;

        // 处理拖拽
        this.dragEventHandler.dragMouseDown(ev, listIndex, this.viewDelegate?.getElementById(`cell-${model.key}`), clearanceIndex);

        this.mouseEventHandler.handleMouseDown(ev, listIndex, model);

        this.services.sectionManager!.selectedSectionIndex = -1;
        this.clearClearanceSelectIndex();
    };

    public handleSectionTitleMouseDown = (event: MouseEvent, sectionIndex: number): void => {
        const clearanceIndex = ClearanceUtils.calcClearanceIndexFromSectionIndex(sectionIndex, this.model.sectionList);
        // 处理拖拽
        this.dragEventHandler.onSectionTitleDragMouseDown(event, sectionIndex, clearanceIndex, (isDraggingSection) => {
            this.updateModel<PcLeftThumbnailListModel>({
                isDraggingSection,
            });
        });

        this.services.sectionManager!.selectedSectionIndex = sectionIndex;
        this.clearClearanceSelectIndex();
    };

    // 母版编辑旧点击事件，目前没有再使用，待删除
    public handleLeftClick = (ev, listIndex: number, model: ISlideModel) => {
        this.mouseEventHandler.handleLeftClick(listIndex, model);
        this.clearClearanceSelectIndex();
    };

    public handleMouseEnter = (event, index) => {
        if (this.services.historyManager?.isShowingHistory) {
            const top = event.currentTarget.offsetTop;
            this.updateModel<PcLeftThumbnailListModel>({
                historyTipsSlide: index,
                historyTipsTops: { [index]: top, ...this.model.historyTipsTops },
            });
        }
    };

    public handleMouseLeave = (event, index) => {
        if (index === this.model.historyTipsSlide) {
            this.updateModel<PcLeftThumbnailListModel>({
                historyTipsSlide: -1,
            });
        }
    };

    public getListWrapperName =  (editType: string) => `listWrapper-${editType}`;

    public getSlideCellClass = editType => `slide-cell-${editType}`;

    public getMasterLayoutGroupList() {
        if (!this.model.masterModelList?.length && !this.model.layoutModelList?.length) return [[]];
        const groupedArr: ISlideModel[][] = [];
        let layoutListIndex = -1;
        this.model.masterModelList.forEach((master, masterIndex) => {
            master.listIndex = masterIndex;
            const masterArr: ISlideModel[] = [master];
            groupedArr.push(masterArr);
            this.model.layoutModelList.forEach((layout) => {
                if (master.id === layout.masterId) {
                    layoutListIndex += 1;
                    layout.listIndex = layoutListIndex;
                    masterArr.push(layout);
                }
            });
        });
        return groupedArr;
    }


    public getRenderModelList(): IBaseModel[][] {
        const result: IBaseModel[] = [];
        if (!this.model.sectionList?.length || this.model.editType === EDIT_TYPE.MASTER_LAYOUT) {
            result.push(new GapModel({ gapIndex: 0 }));
            result.push(...(this.model.slideModelList as ISlideModel[]).map((model, slideIndex) => {
                model.listIndex = slideIndex;
                return model;
            }));
            return [result];
        }
        if (this.model.sectionList) {
            let slideIndex = 0;
            if (this.model.isDraggingSection) {
                result.push(new GapModel({ gapIndex: -1 }));
            }
            (this.model.sectionList as ISectionModel[]).forEach((sectionModel, sectionIndex, sectionList) => {
                sectionModel.sectionIndex = sectionIndex;
                result.push(sectionModel);
                let sectionSize = sectionModel.numSlides;
                if (sectionIndex === sectionList.length - 1) {
                    sectionSize = this.getLastSectionSizeForCompatibility(sectionList);
                }
                if (!this.model.isDraggingSection && !this.getSectionManager()?.sectionFoldedState?.[sectionIndex]) {
                    let listIndex = 0;
                    result.push(...(this.model.slideModelList as ISlideModel[])
                        .map((model) => {
                            model.listIndex = listIndex;
                            listIndex += 1;
                            return model;
                        })
                        .slice(slideIndex, slideIndex + sectionSize));
                }
                slideIndex += sectionSize;
            });
            return [result];
        }
        return [];
    }

    public showSectionContextMenu = (sectionIndex: number, e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const menuData = this.getMenuManager()?.rightMenu4Section(sectionIndex);
        const disableIds = this.getMenuManager()?.disableItemIds();
        disableIds && this.addDisabledItemsForSectionContextMenu(disableIds, sectionIndex);
        const offsetObj = this.getSectionContextMenuOffset(e);
        this.handleShowRightMenu(menuData.type, menuData.items, disableIds, {
            top: e.clientY + offsetObj.offsetY,
            left: e.clientX + 8 + offsetObj.offsetX,
            minWidth: 200,
            subMinWidth: 200,
        }, (menuType, itemID, object) => {
            menuData.callback(menuType, itemID, {
                RegionType: RegionType.SLIDELIST,
                sectionIndex,
                TriggerFrom: INSERT_SLIDE_ORIGIN_TYPE.LEFT,
                object,
            }, null);
        });
    };

    public getHistoryManager(): IHistoryManager | undefined {
        return this.services.historyManager;
    }

    public hideAllMenu(): void {
        return this.getMenuManager()?.hideAllMenu();
    }

    public getServices(): IPcLeftThumbnailListServiceDelegates {
        return this.services;
    }

    /**
     * 获取list client高度，需要减去内容区域padding
     * @private
     */
    public get listClientHeight(): number {
        const { paddingTop = 0, paddingBottom = 0 } = this.getCurGroupWrapperStyle() || { };
        return (this.listWrapper?.clientHeight || 0) - paddingTop - paddingBottom;
    }

    protected initWithProps(props?: IPcLeftThumbnailListProps) {
        this.shouldScrollToIndex = -1;

        this.dragEventHandler = new DragEventHandler(this);
        this.dropEventHandler = new DropEventHandler(this);
        this.mouseEventHandler = new MouseEventHandler(this);

        this.model.lockInfo = this.getLockManager()?.lockInfo ?? []; // 这里初始化lock info

        // 放在didAmount中注册的时机可能晚于isFirstSlideShow更改
        eventBus.on(EventKeys.FIRST_PAGE_DISPLAY, () => {
            if (this.isFirstSlideShow()) {
                this.handleFirstSlideShow();
            }
        });
    }

    protected createViewModel(): BaseViewModel<IPcLeftThumbnailListProps> {
        return new PcLeftThumbnailListModel();
    }

    /**
     * 在service中初始化的model数据
     * @protected
     */
    protected getInitModelValue(): Partial<PcLeftThumbnailListModel> | undefined {
        const { slideInnerHeight, renderSlideHeight } = this.computeSlideHeight();
        return {
            slideWidth: SLIDE_WIDTH - getSlideWidthDiff(this.getUiDataService()),
            slideHeight: renderSlideHeight,
            slideInnerHeight,
            slideModelList: this.services.uiDataService?.slideModelList,
            masterModelList: this.services.uiDataService?.masterModelList,
            layoutModelList: this.services.uiDataService?.layoutModelList,
            sectionList: this.services?.uiDataService?.presentation.sectionList,
            currentSlideType: this.services.selectionService?.currentSlideType,
            editType: this.getMasterLayoutEditType(),
            isFirstSlideShow: this.isFirstSlideShow(),
            isShowingHistory: this.services.historyManager?.isShowingHistory ?? false,
            currentSlideIndex: this.services.selectionService?.currentSlideIndex,
            slideClearanceIndex: this.services.selectionService?.slideClearanceIndex,
        };
    }

    private isFirstSlideShow = () => this.services.slideRenderStateManager
        ?.getStatePoints()[IStatePointEnum.firstSlideRender].isCompleted ?? false;

    private getSlideList(): Array<IMasterModel | ILayoutModel> | Array<ISlideModel> {
        if (this.model.editType === EDIT_TYPE.MASTER_LAYOUT) {
            const { masterModelList = [] } = this.getUiDataService() || { };
            return _flatten(masterModelList.map(masterModel => [masterModel, ...masterModel.layoutList]));
        }
        return this.getUiDataService()?.slideModelList ?? [];
    }

    /**
     * render前，通过store更新model中的数据
     */
    private updateModelBeforeRender() {
        this.model.isShowingHistory = this.services.historyManager?.isShowingHistory ?? false; // 更新model状态
        this.model.currentSlideIndex = this.services.selectionService?.currentSlideIndex ?? 0;
    }

    private handleFirstSlideShow = () => {
        this.model.isFirstSlideShow = true;
        const { slideModelList = [] } = this.getUiDataService() || { };
        this.updateModel<PcLeftThumbnailListModel>({
            slideModelList,
        });
    };

    private handleSlideSizeChange = () => {
        const { slideInnerHeight, renderSlideHeight } = this.computeSlideHeight();
        this.updateModel<PcLeftThumbnailListModel>({
            slideHeight: renderSlideHeight,
            slideInnerHeight,
            shouldUpdate: true,
        }, () => {
            this.model.shouldUpdate = false;
        });
    };

    private handleSectionListChange = (sectionList: ISectionModel[] | undefined) => {
        this.updateModel<PcLeftThumbnailListModel>({
            sectionList,
        });
    };

    private handleSectionFoldedStateChange = (sectionFoldedState?: boolean[] | null) => {
        this.updateModel<PcLeftThumbnailListModel>({
            sectionFoldedState: sectionFoldedState ?? [],
        });
    };

    private handleSlideClearanceIndexChange = (slideClearanceIndex: number) => {
        this.updateModel<PcLeftThumbnailListModel>({
            slideClearanceIndex,
        });
    };

    private handleListChange = (listName: string, listData) => {
        if (this.isFirstSlideShow()) {
            if (this.model.editType !== EDIT_TYPE.MASTER_LAYOUT
                && (listName === 'masterModelList' || listName === 'layoutModelList')) {
                return;
            }
            this.updateModel<PcLeftThumbnailListModel>({
                [listName]: listData,
            });
        }
    };

    // eslint-disable-next-line complexity
    private handleMoveSlide(dragInfo) {
        const { outside, finalIndex, isSectionDrag } = dragInfo;
        if (isSectionDrag) {
            return this.handleSectionDrag(dragInfo);
        }
        const fromSlideIndex = this.services.selectionService?.currentSlideIndex ?? 0;
        if (!outside && finalIndex !== -1) {
            if (this.model.editType === EDIT_TYPE.MASTER_LAYOUT) {
                const { presentation } = this.getUiDataService() || { };
                return presentation?.moveLayout(finalIndex + 1); // 母版编辑里间隙开始的位置是第一个版式，跳过了一个母版故 +1
            }
            // 普通编辑态移动 slide
            if (this.getUiDataService()?.presentation.hasSections()) {
                const { sectionList } = this.model;
                const toSlideIndex = ClearanceUtils.calcSlideIndexFromClearanceIndex(finalIndex, sectionList);
                const toSectionIndex = ClearanceUtils.calcSectionIndexFromClearanceIndex(finalIndex, sectionList);
                const fromSectionIndex = ClearanceUtils.calcSectionIndexFromClearanceIndex(fromSlideIndex, sectionList);
                if (toSectionIndex === fromSectionIndex) {
                    if (toSlideIndex === fromSlideIndex && toSlideIndex - 1 === fromSlideIndex) {
                        return;
                    }
                }
                this.getUiDataService()?.presentation.moveSlideToSection(finalIndex, toSlideIndex, toSectionIndex);
                this.services.sectionManager!.sectionFoldedState![toSectionIndex] = false;
            } else if (fromSlideIndex !== finalIndex) {
                this.getUiDataService()?.presentation.moveSlide(finalIndex);
            }
        }
    }

    private handleSectionDrag(dragInfo) {
        const { originalSectionIndex } = dragInfo;
        let { finalIndex } = dragInfo;
        finalIndex += 1;
        if (originalSectionIndex === finalIndex) {
            return;
        }
        this.getUiDataService()
            ?.presentation.getSectionProperty().moveSection(originalSectionIndex, finalIndex);
        this.services.sectionManager
            ?.moveSectionFoldedState(originalSectionIndex, finalIndex);
        const sectionProperty = this.getUiDataService()?.presentation.getSectionProperty();
        if (sectionProperty) {
            const [slideModels, beginIndex] = sectionProperty.getSectionSlideModelsAndBeginSlideIndex(finalIndex);
            this.services.sectionManager?.selectSection(finalIndex, beginIndex, slideModels);
        }
    }

    private handleScrollWhenIndexChange = () => {
        if (this.services.editingStateManager?.keyFlag4Moving !== -1) {
            const key = this.services.editingStateManager?.keyFlag4Moving ?? 0;
            const currentSlideIndex = this.services.selectionService?.currentSlideIndex ?? 0;
            if ([37, 38, 33].includes(key)) {
                if (currentSlideIndex * this.model.slideHeight < this.listWrapper.scrollTop) {
                    this.listWrapper.scrollTop = currentSlideIndex * this.model.slideHeight;
                }
                this.services.editingStateManager!.keyFlag4Moving = -1;
                this.clearClearanceSelectIndex();
            } else if ([39, 40, 34].includes(key)) {
                if ((currentSlideIndex + 1) * this.model.slideHeight
                    > this.listClientHeight + this.listWrapper.scrollTop) {
                    this.listWrapper.scrollTop = ((currentSlideIndex + 1) * this.model.slideHeight)
                        - this.listClientHeight;
                }
                this.clearClearanceSelectIndex();
                this.services.editingStateManager!.keyFlag4Moving = -1;
            } else if (36 === key) {
                this.listWrapper.scrollTop = currentSlideIndex * this.model.slideHeight;
                this.clearClearanceSelectIndex();
                this.services.editingStateManager!.keyFlag4Moving = -1;
            } else if (35 === key) {
                this.listWrapper.scrollTop = ((currentSlideIndex + 1) * this.model.slideHeight)
                    - this.listClientHeight;
                this.clearClearanceSelectIndex();
                this.services.editingStateManager!.keyFlag4Moving = -1;
            }
        }
    };

    private scrollToSlideIndex(index) {
        if (this.shouldScroll(index)) { // scroll区域大于可见区域
            const scrollTopInFrame = this.listWrapper.scrollTop;
            const scrollBottomInFrame = this.listWrapper.scrollTop + this.listClientHeight;
            const slideTopInScroll = this.getTopScrollMinusFoldedSlides(index);
            const slideBottomInScroll = slideTopInScroll + this.model.slideHeight;
            if (slideTopInScroll < scrollTopInFrame) { // 在上面
                this.listWrapper.scrollTop = slideTopInScroll;
            } else if (slideBottomInScroll > scrollBottomInFrame) { // 在下面
                this.listWrapper.scrollTop = slideBottomInScroll - this.listClientHeight;
            }
        }
    }

    private shouldScroll(index) {
        return this.listWrapper
            && index >= 0
            && this.model.slideHeight * 2 <= this.listWrapper.scrollHeight // 至少可以容下两个才涉及到滚动
            && this.listWrapper.scrollHeight > this.listClientHeight
            && this.services.selectionService?.slideClearanceIndex === -1;
    }

    private getTopScrollMinusFoldedSlides(slideIndex: number): number {
        let result = slideIndex * this.model.slideHeight;
        const sectionManager = this.getSectionManager();
        if (!this.model.sectionList || this.model.sectionList.length <= 0 || !sectionManager?.sectionFoldedState) {
            return result;
        }
        const sectionIndex = ClearanceUtils.calcSectionIndexFromSlideIndex(slideIndex, this.model.sectionList);
        result += sectionIndex * SectionTitle.SectionTitleHeight;
        const toBeSubtractedHeight = sectionManager.sectionFoldedState
            .slice(0, sectionIndex)
            .reduce((accHeight, isFolded, curSectionIndex) => {
                if (isFolded) {
                    accHeight += this.model.sectionList[curSectionIndex].numSlides * this.model.slideHeight;
                }
                return accHeight;
            }, 0);
        return Math.max(0, result - toBeSubtractedHeight);
    }

    private handleCurSlideChange = (currentSlideIndex) => {
        this.updateModel<PcLeftThumbnailListModel>({
            currentSlideIndex,
        });
        if (this.getMasterLayoutEditService()?.isMasterEditType) {
            this.handleCurSlideChangeWhenMasterEditType(currentSlideIndex);
        } else {
            if (this.getUiDataService()?.slideModelList.length !== this.model.slideModelList.length) {
                this.shouldScrollToIndex = currentSlideIndex;
            } else {
                setTimeout(() => {
                    this.scrollToSlideIndex(currentSlideIndex);
                }, 10);
            }
        }
        if (this.services.selectionService?.currentSlide) {
            // slidelist有选中态时，才去通知是否可以解析动画数据
            this.services.selectionService?.currentSlide?.convertAnimDataSdkToUi();
            this.services.selectedAnims?.clear();
        } else {   // 没有选中态时，  收起侧边栏
            const { rightSideBarManager } = this.services;
            if (rightSideBarManager) {
                rightSideBarManager.setIsAnimationEditorShown(false);
                rightSideBarManager.setIsSelectWindowShown(false);
            }
        }

        this.handleScrollWhenIndexChange();
    };

    /**
     * 处理母版编辑状态下的 slideIndex 变化
     * 主要逻辑是从 currentSlideIndex 转换到真正的 index
     * @param currentSlideIndex
     */
    private handleCurSlideChangeWhenMasterEditType(currentSlideIndex) {
        if (this.services.selectionService?.currentSlideType === UIModelType.MASTER) {
            const master = this.services.selectionService?.currentSlide;
            let index = -1;
            this.getUiDataService()?.masterModelList.find((masterModel) => {
                index += 1;
                if (masterModel === master) {
                    return true;
                }
                index += masterModel.layoutList.length;
                return false;
            });
            setTimeout(() => {
                this.scrollToSlideIndex(index);
            }, 10);
        } else {
            const layout = this.services.selectionService?.currentSlide;
            const master = layout?.getRelationMasterModel();
            const masterLen = (this.getUiDataService()?.masterModelList.indexOf(master) ?? 0) + 1;
            setTimeout(() => {
                this.scrollToSlideIndex(masterLen + currentSlideIndex);
            }, 10);
        }
    }

    private handleUnLock = (unlockInfo) => {
        let copyLockInfo = this.model.lockInfo.concat();
        unlockInfo.forEach((unlock) => {
            copyLockInfo = copyLockInfo.filter(lock => lock.slide.id !== unlock.slide.id);
        });
        this.updateModel<PcLeftThumbnailListModel>({
            lockInfo: copyLockInfo,
        });
    };

    private changeDraggingSlides = () => {
        for (let i = 0; i < this.draggingArray.length; i++) {
            const div: HTMLDivElement = this.draggingArray[i].cloneDom;
            if (div) {
                const [x, y] = this.dragPos;
                div.style.transform = `translate(${x + 25}px, ${((this.draggingArray.length - i) * 4) + y}px) scale(1.1)`;
                div.style.width = `${this.model.slideWidth}px`;
                div.style.height = `${this.model.slideHeight}px`;
                div.style.position = 'absolute';
                if (!div.parentNode) {
                    this.draggingDiv.appendChild(div);
                }
            }
        }
    };

    private changeDraggingSectionTitle = () => {
        if (!this.draggingArray || this.draggingArray.length === 0) {
            return;
        }
        const div: HTMLDivElement = this.draggingArray[0].cloneDom;
        if (div) {
            const [x, y] = this.dragPos;
            div.style.transform = `translate(${x}px, ${((this.draggingArray.length - 0) * 4) + y}px)`;
            div.style.width = `${this.model.sectionWidth}px`;
            div.style.position = 'absolute';
            if (!div.parentNode) {
                this.draggingDiv.appendChild(div);
            }
        }
    };

    private get listWrapper() {
        return this.viewDelegate?.getElementById(this.getListWrapperName(this.model.editType));
    }

    private get draggingDiv() {
        return this.viewDelegate?.getElementById('draggingDiv');
    }

    private getSlideGroupList() {
        if (!this.model.slideModelList?.length) return [];
        return this.model.slideModelList.map((model, slideIndex) => {
            model.listIndex = slideIndex;
            return model;
        });
    }

    private handleShowRightMenu = async (type, items, disabledIds, props, callback) => {
        const RightMenuManager = await this.services.rightMenuManager?.promise();
        RightMenuManager?.showMenu(type, items, disabledIds, props, (...args) => {
            RightMenuManager?.hideMenu(type);
            callback(...args);
        });
    };

    private getSectionContextMenuOffset(e: MouseEvent): { offsetX: number, offsetY: number } {
        return { offsetX: 0, offsetY: 0 };
    }

    private addDisabledItemsForSectionContextMenu(disableIds: any[], sectionIndex: number): void {
        const { sectionList } = this.model;
        if ((sectionList?.length ?? 0) > sectionIndex && sectionList[sectionIndex].numSlides === 0) {
            disableIds.push({ id: ActionType.DELETE_SECTION_AND_SLIDES });
        }
        if (sectionIndex === 0 && (sectionList?.length ?? 0) >= 1 && sectionList[0].numSlides > 0) {
            // 第一节
            disableIds.push({ id: ActionType.DELETE_SECTION }, { id: ActionType.MOVE_SECTION_UP });
        }
        if (sectionIndex === (sectionList?.length ?? 0) - 1) {
            // 最后一节
            disableIds.push({ id: ActionType.MOVE_SECTION_DOWN });
        }
    }

    private getSectionProperty(): ISectionProperty | undefined {
        return this.getUiDataService()?.presentation.getSectionProperty();
    }

    private getSectionManager(): ISectionManager | undefined {
        return this.services.sectionManager;
    }

    private getLastSectionSizeForCompatibility(sectionList: ISectionModel[]): number {
        // 有可能数据有损坏，导致 sectionList 的 numSlides 叠加不等于总页面数
        // 因此这里做数据兼容，把不存在于 section 中的页面全都归到最后一个 section 中
        const prevSectionLength = sectionList
            .slice(0, sectionList.length - 1).reduce((l, section) => l + section.numSlides, 0);
        const sectionLength = this.getSlideList().length - prevSectionLength;
        return sectionLength;
    }

    private fixDragIndexOnSectionDrag(dragInfo, dragIndex: number): number {
        let currentDragIndex = ClearanceUtils.calcClearanceIndexFromSectionIndex(dragIndex, this.model.sectionList);
        currentDragIndex += this.model.sectionList[dragIndex].numSlides ?? 0;
        return currentDragIndex;
    }

    private getUiDataService(): IUiDataService | undefined {
        return this.services?.uiDataService;
    }

    private getUserPriviledgeService(): IUserPriviledgeService | undefined {
        return this.services.userPriviledgeService;
    }

    private getMasterLayoutEditService(): IMasterLayoutEditService | undefined {
        return this.services.masterLayoutEditService;
    }

    private getMenuManager(): IMenuManager | undefined {
        return this.services.menuManager;
    }

    private getShouldConvertModels(visibleCellIDs: Array<string>): Array<ISlideModel> {
        const dataList = this.getDataList();
        const dataListLength = dataList.length;
        let start = Infinity;
        let end = -1;
        visibleCellIDs.forEach((id) => {
            const index = dataList.findIndex(model => model.key === id);
            if (index < start) {
                start = index;
            }
            if (index > end) {
                end = index;
            }
        });
        if (start > end) {
            return [];
        }
        if (end + SLIDE_LIST_MORE_CONVERT_PAGE_NUMBER < dataListLength) {
            end += SLIDE_LIST_MORE_CONVERT_PAGE_NUMBER;
        } else {
            end = dataListLength - 1;
        }
        return dataList.slice(start, end + 1);
    }

    private getLockManager() {
        return OnlineEditServices.CooperationLockManager();
    }

    private computeSlideHeight(): { renderSlideHeight: number, slideInnerHeight: number } {
        const { slideWidth = 0, slideHeight = 0 } = this.services.uiDataService?.presentation.props || { };
        const slideInnerHeight = SLIDE_WIDTH / slideWidth * slideHeight;
        const renderSlideHeight = slideInnerHeight + 16;
        return { slideInnerHeight, renderSlideHeight };
    }

    private getMasterLayoutEditType(): string {
        if (browser.isMobile || !this.services.userPriviledgeService?.isEditable()) {
            return EDIT_TYPE.SLIDE;
        }
        return this.services.masterLayoutEditService?.editType ?? EDIT_TYPE.SLIDE;
    }
}
